import { differenceInCalendarDays, startOfDay } from 'date-fns';
import type { EpcScheduleTaskRow } from '../../types/scheduleTask';
import { parseTaskDay } from './epcScheduleDatesDisplay';
import { parsePredecessorLinks, resolvePredecessorTaskIndex } from './epcScheduleDependencies';
import { parsePercentComplete } from './epcScheduleMsProjectFormat';

export type EpcTaskStatus = 'done' | 'inProgress' | 'slipping' | 'notStarted' | 'blocked';

export const TASK_STATUS_LABEL: Record<EpcTaskStatus, string> = {
  done: 'Done',
  inProgress: 'In progress',
  slipping: 'In progress · slipping',
  notStarted: 'Not started',
  blocked: 'Blocked',
};

/** Actual % complete as a number (0 when unset). */
export function taskPercent(task: EpcScheduleTaskRow): number {
  return parsePercentComplete(task.percentComplete) ?? 0;
}

/** Linear planned-% as of `today` from start/finish. Null when either date is missing. */
export function plannedTodayPercent(task: EpcScheduleTaskRow, today: Date): number | null {
  const s = parseTaskDay(task.start);
  const f = parseTaskDay(task.finish);
  if (!s || !f) return null;
  const day = startOfDay(today);
  if (day < startOfDay(s)) return 0;
  if (day >= startOfDay(f)) return 100;
  const total = Math.max(1, differenceInCalendarDays(f, s));
  const elapsed = differenceInCalendarDays(day, s);
  return Math.max(0, Math.min(100, Math.round((elapsed / total) * 100)));
}

/**
 * Pure status derivation (spec §4.2).
 * blocked = 0% done AND some FS predecessor with % < 100 whose finish is already in the past.
 */
export function deriveTaskStatus(
  tasks: EpcScheduleTaskRow[],
  index: number,
  today: Date
): EpcTaskStatus {
  const task = tasks[index];
  if (!task) return 'notStarted';
  const pct = taskPercent(task);
  if (pct >= 100) return 'done';
  if (pct > 0) {
    const planned = plannedTodayPercent(task, today);
    return planned != null && pct < planned ? 'slipping' : 'inProgress';
  }
  const day = startOfDay(today);
  for (const link of parsePredecessorLinks(task.predecessors)) {
    if (link.linkTypeCode !== 'FS') continue;
    const pi = resolvePredecessorTaskIndex(link, tasks, index);
    if (pi == null) continue;
    const pred = tasks[pi];
    if (!pred) continue;
    const predFinish = parseTaskDay(pred.finish);
    if (taskPercent(pred) < 100 && predFinish && startOfDay(predFinish) < day) return 'blocked';
  }
  return 'notStarted';
}
